import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import BackToTop from "@/components/base/BackToTop";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import BusinessGrid from "@/components/feature/BusinessGrid";
import DirectoryFilters, { type DirectoryFilterState } from "./components/DirectoryFilters";
import { getCategories, getLocations } from "@/services/publicApi";
import { useBusinessSearch } from "@/hooks/useBusinessSearch";
import { subcategories } from "@/mocks/subcategories";
import { popularSearches } from "@/mocks/categories";
import { categoryPath } from "@/utils/slug";
import {
  COVERED_REGIONS,
  districtNameForSlug,
  normalizeDistrict,
  talukNameForSlug,
} from "@/config/regions";
import type { BusinessSearchParams, Category, Location } from "@/types";

const PAGE_SIZE = 9;

const sortOptions = [
  { value: "relevance", label: "Best match" },
  { value: "rating", label: "Highest rated" },
  { value: "reviews", label: "Most reviewed" },
  { value: "name", label: "Name (A–Z)" },
];

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  useEffect(() => {
    Promise.all([getCategories(), getLocations()])
      .then(([c, l]) => {
        setCategories(c);
        setLocations(l);
      })
      .catch(() => {
        // Filter lists are non-critical; search still works without them.
      });
  }, []);

  const legacyLocation = params.get("location") || "";

  // Backwards compatibility: older links use a single ?location= param. Map it
  // to the new district/taluk pair (a district row becomes a district filter, a
  // town row becomes a taluk filter).
  const legacyPlace = useMemo<{ district?: string; town?: string }>(() => {
    if (!legacyLocation) return {};
    const direct = COVERED_REGIONS.find(
      (r) => r.slug === legacyLocation || normalizeDistrict(r.district) === normalizeDistrict(legacyLocation),
    );
    if (direct) return { district: direct.slug };
    const loc = locations.find((l) => String(l.id) === legacyLocation || l.slug === legacyLocation);
    if (!loc) return {};
    if (normalizeDistrict(loc.name) === normalizeDistrict(loc.district)) {
      const region = COVERED_REGIONS.find(
        (r) => normalizeDistrict(r.district) === normalizeDistrict(loc.district),
      );
      return region ? { district: region.slug } : {};
    }
    return { town: loc.slug };
  }, [legacyLocation, locations]);

  const state: DirectoryFilterState = {
    q: params.get("q") || "",
    category: params.get("category") || "",
    subcategory: params.get("subcategory") || "",
    district: params.get("district") || legacyPlace.district || "",
    town: params.get("town") || legacyPlace.town || "",
    sort: params.get("sort") || "relevance",
    openNow: params.get("open") === "1",
    minRating: Number(params.get("rating") || 0),
  };

  const page = Math.max(1, Number(params.get("page") || 1));

  const update = (patch: Partial<DirectoryFilterState>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      const isEmpty = v === "" || v === false || v === 0 || (k === "sort" && v === "relevance");
      if (isEmpty) {
        next.delete(k);
        return;
      }
      next.set(k, k === "openNow" ? "1" : String(v));
    });
    next.delete("page");
    next.delete("location");
    setParams(next, { replace: true });
  };

  const reset = () => setParams(new URLSearchParams(), { replace: true });

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resolvedCategory = useMemo(
    () => categories.find((c) => c.id === state.category || c.slug === state.category) || null,
    [categories, state.category],
  );
  const resolvedSubcategory = useMemo(
    () => subcategories.find((s) => s.slug === state.subcategory || s.id === state.subcategory) || null,
    [state.subcategory],
  );

  const availableSubcategories = useMemo(
    () => (resolvedCategory ? subcategories.filter((s) => s.categoryId === resolvedCategory.id) : []),
    [resolvedCategory],
  );

  const { items, total, pages, loading, error, reload } = useBusinessSearch({
    q: state.q,
    category: state.category,
    subcategory: state.subcategory,
    district: state.district,
    town: state.town,
    minRating: state.minRating,
    openNow: state.openNow,
    sort: state.sort as BusinessSearchParams["sort"],
    page,
    perPage: PAGE_SIZE,
  });

  const resolvedDistrictName = districtNameForSlug(state.district);
  const resolvedTalukName = talukNameForSlug(state.town);

  const activeChips: { label: string; patch: Partial<DirectoryFilterState> }[] = [];
  if (state.q) activeChips.push({ label: `"${state.q}"`, patch: { q: "" } });
  if (resolvedCategory) activeChips.push({ label: resolvedCategory.name, patch: { category: "", subcategory: "" } });
  if (resolvedSubcategory) activeChips.push({ label: resolvedSubcategory.name, patch: { subcategory: "" } });
  if (resolvedDistrictName) activeChips.push({ label: `${resolvedDistrictName} district`, patch: { district: "", town: "" } });
  if (resolvedTalukName) activeChips.push({ label: `${resolvedTalukName} taluk`, patch: { town: "" } });
  if (state.openNow) activeChips.push({ label: "Open now", patch: { openNow: false } });
  if (state.minRating > 0) activeChips.push({ label: `${state.minRating}+ rating`, patch: { minRating: 0 } });

  const seoLanding =
    resolvedCategory && state.district ? categoryPath(resolvedCategory.slug, state.district) : null;

  return (
    <>
      <Seo
        title="Business Directory — Search Local Businesses"
        description="Search and filter verified local businesses across Mysore, Mandya, Chamarajanagar and Kodagu by category, subcategory, location, rating and opening hours."
        keywords="Mysore business directory, local business search, Mandya listings, Kodagu businesses"
        path="/directory"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "Directory" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            Business Directory
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
            Search across categories, subcategories and locations in the Mysore region.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {popularSearches.slice(0, 5).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => update({ q: p })}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 hover:border-primary-400 hover:bg-primary-50 whitespace-nowrap cursor-pointer"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-8">
          <DirectoryFilters
            categories={categories}
            subcategories={availableSubcategories}
            state={state}
            onChange={update}
            onReset={reset}
          />

          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-foreground-600">
                {loading ? (
                  "Searching…"
                ) : (
                  <>
                    <span className="font-semibold text-foreground-950">{total}</span> result
                    {total === 1 ? "" : "s"} found
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="sort" className="text-sm text-foreground-500 whitespace-nowrap">
                  Sort by
                </label>
                <select
                  id="sort"
                  value={state.sort}
                  onChange={(e) => update({ sort: e.target.value })}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {activeChips.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {activeChips.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    onClick={() => update(chip.patch)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900 hover:bg-secondary-200 cursor-pointer"
                  >
                    {chip.label}
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs font-semibold text-foreground-500 hover:text-foreground-800 cursor-pointer"
                >
                  Reset
                </button>
              </div>
            )}

            {seoLanding && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                <i className="ri-link text-primary-700"></i>
                <span className="text-sm text-foreground-700">Shareable SEO page for this combination:</span>
                <Link
                  to={seoLanding}
                  className="text-sm font-semibold text-primary-800 hover:text-primary-900 cursor-pointer"
                >
                  {seoLanding}
                </Link>
              </div>
            )}

            <div className="mt-5">
              {error ? (
                <div className="space-y-4">
                  <EmptyState
                    icon="ri-wifi-off-line"
                    title="Couldn't load businesses"
                    description={error}
                  />
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={reload}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i> Try again
                    </button>
                  </div>
                </div>
              ) : loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                  ))}
                </div>
              ) : items.length === 0 ? (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="No businesses match your filters"
                  description="Try removing a filter or searching for a different keyword or location."
                  action={{ label: "Back to Home", to: "/" }}
                />
              ) : (
                <>
                  <BusinessGrid businesses={items} />
                  <Pagination page={page} totalPages={pages} onChange={setPage} />
                </>
              )}
            </div>

            {!loading && !error && items.length > 0 && (
              <div className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading text-base font-bold text-foreground-950">Own a business here?</h2>
                  <p className="mt-1 text-sm text-foreground-600">Claim your free listing and reach thousands of local customers.</p>
                </div>
                <Link
                  to="/list-your-business"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-circle-line"></i> List your business
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <BackToTop showAfter={600} />
    </>
  );
}